"use client"

import { useRef, useState } from "react"
import { motion } from "framer-motion"
import { Maximize2, ExternalLink, X } from "lucide-react"
// Import all Lucide icons for dynamic rendering
import * as LucideIcons from "lucide-react"
import DownloadableFile from "./DownloadableFile"
import "./components/TemplateSelector.css"
import DOMPurify from "dompurify" // Use DOMPurify instead of sanitize-html

// Import components
import StaticTimerPill from "./components/StaticTimerPill"
import TagPill from "./components/TagPill"
import ClickableImage from "./components/ClickableImage"
import { parseContent } from "./utils/text-parser"

// ===== LUCIDE ICON UTILITIES =====

// Function to render Lucide icon from string name
const renderLucideIcon = (iconName, props = {}) => {
  if (!iconName) return null

  // Default props
  const iconProps = {
    size: 24,
    ...props,
  }

  // Check if the icon exists in Lucide
  const IconComponent = LucideIcons[iconName]

  if (IconComponent) {
    return <IconComponent {...iconProps} />
  }

  // Fallback if icon not found
  console.warn(`Icon "${iconName}" not found in Lucide`)
  return null
}

// Function to convert kebab-case to PascalCase for Lucide icon names
const kebabToPascalCase = (kebabString) => {
  if (!kebabString || typeof kebabString !== "string") return ""
  return kebabString
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("")
}

// Function to render icon with background
// Function to render icon with background with positioning options
const renderIconWithBackground = (iconName, options = {}) => {
  const {
    backgroundColor = "#E74C3C",
    iconColor = "white",
    size = 40,
    padding = 8,
    position = "default", // New option: 'default', 'centered', 'top-left', 'top-right', etc.
    positionStyles = {}, // Custom positioning styles
  } = options

  // Convert kebab-case to PascalCase
  const pascalCaseIconName = kebabToPascalCase(iconName)

  const IconComponent = LucideIcons[pascalCaseIconName]

  if (!IconComponent) return null

  // Determine positioning styles based on the position option
  let containerStyles = {
    backgroundColor,
    width: size,
    height: size,
  }

  // Apply positioning styles based on the position option
  if (position === "centered") {
    containerStyles = {
      ...containerStyles,
      position: "relative",
      top: "80%",
      left: "50%",
      transform: "translate(-20%, -90%)",
      zIndex: 10,
    }
  } else if (position === "top-left") {
    containerStyles = {
      ...containerStyles,
      position: "relative",
      top: "12px",
      left: "12px",
      zIndex: 10,
    }
  } else if (position === "top-right") {
    containerStyles = {
      ...containerStyles,
      position: "relative",
      top: "12px",
      right: "12px",
      zIndex: 10,
    }
  }

  // Apply any custom position styles (these will override the preset positions)
  containerStyles = {
    ...containerStyles,
    ...positionStyles,
  }

  return (
    <div className="relative flex items-center justify-center rounded-full" style={containerStyles}>
      <IconComponent size={size - padding * 2} color={iconColor} strokeWidth={2.5} />
    </div>
  )
}

// ===== SIMPLIFIED STYLE UTILITIES =====

// Consolidated style utility that handles all style properties from JSON
const getStyleFromProps = (props, defaultValues = {}) => {
  if (!props) return defaultValues

  // Create a style object that will be applied directly
  const styles = {}

  // Direct style properties that can be applied as-is
  if (props.backgroundColor && props.backgroundColor !== "transparent") {
    styles.backgroundColor = props.backgroundColor
  }

  if (props.color || props.textColor) {
    styles.color = props.color || props.textColor
  }

  if (props.borderColor) {
    styles.borderColor = props.borderColor
  }

  if (props.borderWidth) {
    styles.borderWidth = props.borderWidth
  }

  if (props.borderStyle) {
    styles.borderStyle = props.borderStyle
  }

  if (props.borderLeft) {
    styles.borderLeft = props.borderLeft
  }

  if (props.borderRight) {
    styles.borderRight = props.borderRight
  }

  if (props.borderTop) {
    styles.borderTop = props.borderTop
  }

  if (props.borderBottom) {
    styles.borderBottom = props.borderBottom
  }

  // Handle margin properties
  if (props.marginTop) {
    styles.marginTop = props.marginTop
  }

  if (props.marginBottom) {
    styles.marginBottom = props.marginBottom
  }

  if (props.marginLeft) {
    styles.marginLeft = props.marginLeft
  }

  if (props.marginRight) {
    styles.marginRight = props.marginRight
  }

  // Handle height and width properties
  if (props.height) {
    styles.height = props.height
  }

  if (props.minHeight) {
    styles.minHeight = props.minHeight
  }

  if (props.maxHeight) {
    styles.maxHeight = props.maxHeight
  }

  if (props.width) {
    styles.width = props.width
  }

  if (props.minWidth) {
    styles.minWidth = props.minWidth
  }

  if (props.maxWidth) {
    styles.maxWidth = props.maxWidth
  }

  // Handle any other direct style properties
  Object.keys(props).forEach((key) => {
    // Only add if it's a valid CSS property and not already handled
    if (
      (![
        "className",
        "padding",
        "margin",
        "rounded",
        "textSize",
        "fontSize",
        "fontWeight",
        "textAlignment",
        "textColor",
        "backgroundColor",
        "height",
        "width",
        "minHeight",
        "maxHeight",
        "minWidth",
        "maxWidth",
      ].includes(key) &&
        typeof props[key] === "string") ||
      typeof props[key] === "number"
    ) {
      // Convert camelCase to kebab-case for CSS properties
      const cssKey = key.replace(/([A-Z])/g, "-$1").toLowerCase()
      styles[cssKey] = props[key]
    }
  })

  return styles
}

// Get CSS classes from style properties
const getClassesFromProps = (props, defaults = {}) => {
  if (!props) return Object.values(defaults).filter(Boolean).join(" ")

  const classes = []

  // Padding
  if (props.padding) {
    classes.push(props.padding.startsWith("p-") ? props.padding : `p-${props.padding}`)
  } else if (defaults.padding) {
    classes.push(defaults.padding)
  }

  // Margin - IMPORTANT: This is the key part for handling margin classes
  if (props.margin) {
    // Handle all margin classes (mb-X, mt-X, mx-X, my-X, etc.)
    if (props.margin.startsWith("m") && (props.margin.includes("-") || props.margin === "m-0")) {
      classes.push(props.margin)
    } else {
      classes.push(`m-${props.margin}`)
    }
  } else if (defaults.margin) {
    classes.push(defaults.margin)
  }

  // Rounded corners
  if (props.rounded) {
    if (props.rounded === "none") {
      // No class for no rounding
    } else if (["sm", "md", "lg", "xl", "full"].includes(props.rounded)) {
      classes.push(`rounded-${props.rounded}`)
    } else if (props.rounded.startsWith("rounded-")) {
      classes.push(props.rounded)
    } else {
      classes.push(`rounded-${props.rounded}`)
    }
  } else if (defaults.rounded) {
    classes.push(defaults.rounded)
  }

  // Font size
  if (props.fontSize || props.textSize) {
    const size = props.fontSize || props.textSize
    if (size.startsWith("text-")) {
      classes.push(size)
    } else if (["xs", "sm", "base", "lg", "xl", "2xl", "3xl", "4xl", "5xl", "6xl"].includes(size)) {
      classes.push(`text-${size}`)
    } else {
      classes.push(size)
    }
  } else if (defaults.fontSize) {
    classes.push(defaults.fontSize)
  }

  // Font weight
  if (props.fontWeight) {
    if (["normal", "medium", "semibold", "bold"].includes(props.fontWeight)) {
      classes.push(`font-${props.fontWeight}`)
    } else {
      classes.push(props.fontWeight)
    }
  } else if (defaults.fontWeight) {
    classes.push(defaults.fontWeight)
  }

  // Text alignment
  if (props.textAlignment) {
    if (["left", "center", "right"].includes(props.textAlignment)) {
      classes.push(`text-${props.textAlignment}`)
    } else {
      classes.push(props.textAlignment)
    }
  } else if (defaults.textAlignment) {
    classes.push(defaults.textAlignment)
  }

  // Spacing
  if (props.spacing) {
    const spacingMap = {
      xs: "gap-1",
      sm: "gap-2",
      md: "gap-3",
      lg: "gap-4",
      xl: "gap-6",
      none: "gap-0",
      normal: "gap-3",
      large: "gap-6",
    }

    if (spacingMap[props.spacing]) {
      classes.push(spacingMap[props.spacing])
    } else if (props.spacing.startsWith("gap-")) {
      classes.push(props.spacing)
    } else {
      classes.push(`gap-${props.spacing}`)
    }
  } else if (defaults.spacing) {
    classes.push(defaults.spacing)
  }

  // Global spacing (for containers)
  if (props.globalSpacing) {
    const spacingMap = {
      compact: "space-y-0",
      tight: "space-y-1",
      normal: "space-y-2",
      loose: "space-y-4",
      none: "space-y-0",
    }

    if (spacingMap[props.globalSpacing]) {
      classes.push(spacingMap[props.globalSpacing])
    } else if (props.globalSpacing.startsWith("space-y-")) {
      classes.push(props.globalSpacing)
    } else {
      classes.push(`space-y-${props.globalSpacing}`)
    }
  } else if (defaults.globalSpacing) {
    classes.push(defaults.globalSpacing)
  }

  // Alignment
  if (props.alignment) {
    const alignmentMap = {
      start: "justify-start",
      center: "justify-center",
      end: "justify-end",
      between: "justify-between",
      around: "justify-around",
      evenly: "justify-evenly",
    }

    if (alignmentMap[props.alignment]) {
      classes.push(alignmentMap[props.alignment])
    } else {
      classes.push(props.alignment)
    }
  } else if (defaults.alignment) {
    classes.push(defaults.alignment)
  }

  // Flex properties
  if (props.flex) {
    classes.push(`flex-${props.flex}`)
  }

  if (props.grow) {
    classes.push(`grow-${props.grow}`)
  }

  if (props.shrink) {
    classes.push(`shrink-${props.shrink}`)
  }

  // Theme (predefined color combinations)
  if (props.theme) {
    const themeMap = {
      blue: "bg-blue-100 border-blue-300 text-blue-800",
      green: "bg-green-100 border-green-300 text-green-800",
      red: "bg-red-100 border-red-300 text-red-800",
      yellow: "bg-yellow-100 border-yellow-300 text-yellow-800",
      purple: "bg-purple-100 border-purple-300 text-purple-800",
      pink: "bg-pink-100 border-pink-300 text-pink-800",
      orange: "bg-orange-100 border-orange-300 text-orange-800",
      teal: "bg-teal-100 border-teal-300 text-teal-800",
      indigo: "bg-indigo-100 border-indigo-300 text-indigo-800",
      gray: "bg-gray-100 border-gray-300 text-gray-800",
    }

    if (themeMap[props.theme]) {
      classes.push(themeMap[props.theme])
    } else {
      classes.push(props.theme)
    }
  } else if (defaults.theme) {
    classes.push(defaults.theme)
  }

  // Add any custom classes
  if (props.className) {
    classes.push(props.className)
  }

  return classes.filter(Boolean).join(" ")
}

const getLayoutClasses = (layout = {}) => {
  if (!layout) return ""

  const { type = "grid", columns = 1, spacing = "normal", alignment } = layout

  // Determine the grid class based on layout type
  let layoutClass = ""

  if (type === "grid") {
    // Use auto-fit or auto-fill for responsive grids
    if (layout.responsive) {
      const minWidth = layout.minColumnWidth || "250px"
      layoutClass = `grid grid-cols-1 md:grid-template-columns-${minWidth}`
    } else if (layout.customGrid) {
      // Support for custom grid templates
      layoutClass = `grid ${layout.customGrid}`
    } else {
      layoutClass = `grid grid-cols-1 md:grid-cols-${Math.min(columns, 6)}`
    }
  } else if (type === "flex") {
    layoutClass = `flex ${layout.flexDirection || "flex-wrap"}`

    // Add flex grow/shrink settings if provided
    if (layout.flexGrow) layoutClass += ` grow-${layout.flexGrow}`
    if (layout.flexShrink) layoutClass += ` shrink-${layout.flexShrink}`
  } else if (type === "fullWidth") {
    layoutClass = "w-full"
  } else if (type === "masonry") {
    layoutClass = "masonry-grid" // Custom class for masonry layout
  } else if (type === "custom") {
    // Allow completely custom layout class
    layoutClass = layout.customClass || ""
  } else {
    layoutClass = "space-y-0"
  }

  // Add spacing
  const spacingClass = getClassesFromProps({ spacing })

  // Add alignment
  const alignmentClass = alignment ? getClassesFromProps({ alignment }) : ""

  return `${layoutClass} ${spacingClass} ${alignmentClass}`.trim()
}

// ===== REUSABLE COMPONENTS =====

const renderDownloadableFile = (file) => {
  if (!file) return null

  return (
    <div className="my-4">
      <DownloadableFile
        filePath={file.filePath}
        fileName={file.fileName}
        fileType={file.fileType}
        showPreview={file.showPreview !== false}
        previewUrl={file.previewUrl}
        buttonText={file.buttonText || "Download"}
        description={file.description}
      />
    </div>
  )
}

// Render list items with consistent styling
const renderListItems = (items, options = {}) => {
  if (!items || !items.length) return null

  const { isNumbered = false, highlightItem, currentLine } = options

  return (
    <ul className={`${isNumbered ? "numbered-list" : "list-none"} space-y-1 m-0 p-0`}>
      {items.map((item, i) => {
        // Check if this item should be highlighted based on SRT line
        const isItemHighlightedBySrt =
          typeof item === "object" &&
          item.highlightAtSrtIndex !== undefined &&
          item.highlightAtSrtIndex === currentLine + 1

        // Use either direct highlight or SRT-based highlight
        const isItemHighlighted = highlightItem === i || isItemHighlightedBySrt

        // Get the text content from either string or object
        const itemContent = typeof item === "string" ? item : item.text
        const itemIcon = typeof item === "string" ? "•" : item.icon || "•"

        if (isNumbered) {
          return (
            <li
              key={i}
              className={`numbered-list-item ${isItemHighlighted ? "numbered-list-item-highlighted" : ""} m-0`}
              data-highlighted={isItemHighlighted ? "true" : "false"}
            >
              <span className="numbered-list-marker"></span>
              <span>{itemContent}</span>
            </li>
          )
        }

        return (
          <li
            key={i}
            className={`list-item ${isItemHighlighted ? "list-item-highlighted" : ""} m-0`}
            data-highlighted={isItemHighlighted ? "true" : "false"}
          >
            <span className={`list-marker ${isItemHighlighted ? "list-marker-highlighted" : ""}`}>
              {isItemHighlighted ? "→" : itemIcon}
            </span>
            <span>{itemContent}</span>
          </li>
        )
      })}
    </ul>
  )
}

// Render links with consistent styling
const renderLinks = (links) => {
  if (!links || !links.length) return null

  return (
    <div className="mt-3 space-y-2">
      {links.map((link, j) => (
        <a
          key={j}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-blue-600 hover:underline"
        >
          <ExternalLink size={16} className="mr-1" />
          {link.text || link.url}
        </a>
      ))}
    </div>
  )
}

// ===== MAIN COMPONENTS =====

// Base Template component
const BaseTemplate = ({ content, children }) => {
  // Only show timer if showTimer is true in content
  const showTimer = content?.showTimer === true

  return (
    <div className="template-base">
      <div className="flex flex-col sm:flex-row mb-1 justify-between items-start">
        {/* Title on the left */}
        <div className="template-title">
          {content?.title && <h2 className="text-2xl font-bold">{content.title}</h2>}
        </div>

        {/* Tags and timer horizontally aligned at the right */}
        <div className="flex flex-row items-center gap-1 mt-0 sm:mt-0 flex-wrap justify-end">
          {/* Individual tag pills */}
          {content?.tags &&
            content.tags.map((tag, index) => (
              <TagPill
                key={index}
                text={typeof tag === "string" ? tag : tag.text}
                icon={typeof tag === "string" ? null : tag.icon}
                backgroundColor={typeof tag === "string" ? undefined : tag.backgroundColor}
                textColor={typeof tag === "string" ? undefined : tag.textColor}
              />
            ))}

          {/* Timer pill */}
          {showTimer && <StaticTimerPill minutes={content?.timerMinutes || 10} seconds={content?.timerSeconds || 0} />}
        </div>
      </div>

      {/* Progress tracker if defined */}
      {content?.progress && content.progress.steps && (
        <ProgressTracker steps={content.progress.steps} currentStep={content.progress.currentStep || 0} />
      )}

      <div className="template-content">
        {children}
        {content?.downloadableFile && renderDownloadableFile(content.downloadableFile)}
      </div>
    </div>
  )
}

// Progress Tracker component
const ProgressTracker = ({ steps, currentStep }) => {
  return (
    <div className="flex items-center justify-between w-full mb-6">
      {steps.map((step, index) => (
        <div key={index} className="flex items-center">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full ${
              index < currentStep
                ? "bg-green-500 text-white"
                : index === currentStep
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-500"
            }`}
          >
            {index < currentStep ? "✓" : index + 1}
          </div>
          {index < steps.length - 1 && (
            <div className={`h-1 w-12 ${index < currentStep ? "bg-green-500" : "bg-gray-200"}`}></div>
          )}
        </div>
      ))}
    </div>
  )
}

// Row component for multi-row layouts
const Row = ({ row, index, currentLine }) => {
  if (!row) return null

  // Get row styling
  const rowStyle = row.style || {}

  // Get CSS classes from row properties
  const rowClasses = getClassesFromProps(
    { ...row, ...rowStyle },
    { padding: "p-0", rounded: "rounded-lg", fontSize: "text-base", fontWeight: "font-normal" },
  )

  // Get inline styles from row properties
  const rowStyles = getStyleFromProps({ ...row, ...rowStyle })

  // Get layout classes for this row
  const layoutClass = getLayoutClasses(row.layout || {})

  return (
    <div className={`row ${rowClasses} ${layoutClass}`} style={rowStyles}>
      {row.columns &&
        row.columns.map((column, colIndex) => (
          <ColumnContent key={colIndex} column={column} index={colIndex} currentLine={currentLine} />
        ))}
    </div>
  )
}

// Column Content component - extracted to be reusable
const ColumnContent = ({ column, index, currentLine }) => {
  // Get column styling
  const colStyle = column.style || {}

  // Check if column should be highlighted
  const isHighlighted =
    column.highlight ||
    (column.subtitleRange && currentLine >= column.subtitleRange[0] && currentLine <= column.subtitleRange[1])

  // Get CSS classes from column properties
  const colClasses = getClassesFromProps({ ...column, ...colStyle }, { padding: "p-4", rounded: "rounded-lg" })

  // Get inline styles from column properties
  const colStyles = {
    ...getStyleFromProps({ ...column, ...colStyle }),
    // Support for custom grid positioning
    gridColumn: column.gridColumn || "",
    gridRow: column.gridRow || "",
    // Support for custom flex settings
    flex: column.flex || "",
    order: column.order || "",
    // Support for custom positioning
    position: column.position || "",
    top: column.top || "",
    left: column.left || "",
    right: column.right || "",
    bottom: column.bottom || "",
    zIndex: column.zIndex || "",
    // Support for transforms
    transform: column.transform || "",
  }
  // Get title classes
  const titleClasses = getClassesFromProps(
    {
      fontSize: column.titleFontSize,
      fontWeight: column.titleFontWeight || "semibold",
      textAlignment: column.titleAlignment,
    },
    { fontSize: "text-lg", fontWeight: "font-semibold" },
  )

  // Get description classes
  const descClasses = getClassesFromProps(
    {
      fontSize: column.descriptionFontSize,
      fontWeight: column.descriptionFontWeight,
      textAlignment: column.descriptionAlignment,
    },
    { fontSize: "text-base" },
  )

  // Animation settings for highlighted columns
  const animation = isHighlighted
    ? {
        scale: 1.02,
        transition: { duration: 0.3 },
      }
    : {}

  // Render Lucide icon or custom icon
  const renderIcon = () => {
    // If no icon, return null
    if (!column.icon) return null

    // If iconStyle is circle-bg, render icon with background
    if (column.iconStyle === "circle-bg") {
      return renderIconWithBackground(column.icon, {
        backgroundColor: column.iconBgColor || "#E74C3C",
        iconColor: column.iconColor || "white",
        size: Number.parseInt(column.iconSize) || 40,
        padding: 8,
        position: column.iconPosition || "default",
        positionStyles: column.iconPositionStyles || {},
      })
    }

    // If icon is a string and contains a dash, assume it's a Lucide icon name
    if (typeof column.icon === "string" && column.icon.indexOf("-") > -1) {
      return renderLucideIcon(kebabToPascalCase(column.icon), {
        size: Number.parseInt(column.iconSize) || 24,
        color: column.iconColor || "currentColor",
        className: column.iconClassName || "",
      })
    }

    // Otherwise, just render the icon as is (emoji or other content)
    return (
      <div className={`text-5xl ${column.iconClassName || ""}`} style={{ color: column.iconColor || "currentColor" }}>
        {column.icon}
      </div>
    )
  }

  return (
    <motion.div
      className={`column ${colClasses} ${isHighlighted ? "column-highlighted" : ""}`}
      style={colStyles}
      animate={animation}
    >
      {(column.icon || column.title) && (
        <div className={`mb-1 ${column.titleAlignment ? `text-${column.titleAlignment}` : ""}`}>
          {column.icon && (
            <div className={`mb-1 ${column.titleAlignment ? `text-${column.titleAlignment}` : "text-left"}`}>
              {renderIcon()}
            </div>
          )}
          {column.title && <h3 className={`${titleClasses} m-0`}>{column.title}</h3>}
        </div>
      )}

      {column.description && <p className={`${descClasses} m-0 w-full`}>{parseContent(column.description)}</p>}

      {column.content && (
        <div className="column-content">
          {typeof column.content === "string" ? <p className="m-0">{parseContent(column.content)}</p> : column.content}
        </div>
      )}

      {/* Render nested rows if present */}
      {column.rows &&
        column.rows.map((row, rowIndex) => <Row key={rowIndex} row={row} index={rowIndex} currentLine={currentLine} />)}

      {/* Render downloadable file */}
      {column.downloadableFile && renderDownloadableFile(column.downloadableFile)}

      {/* Render list items */}
      {column.listItems && (
        <div className="column-items">
          {renderListItems(column.listItems, {
            isNumbered: column.listType === "numbered",
            highlightItem: column.highlightItem,
            currentLine: currentLine,
          })}
        </div>
      )}

      {/* Render links */}
      {column.links && renderLinks(column.links)}

      {/* Render image if present */}
      {column.imageSrc && (
        <div className="m-0 flex flex-col items-center">
          <ClickableImage
            src={column.imageSrc || "/placeholder.svg"}
            alt={column.imageAlt || "Illustration"}
            className="max-w-full rounded-lg shadow-sm"
          />
          {column.imageCaption && <p className="text-sm text-gray-500 mt-2 text-center">{column.imageCaption}</p>}
        </div>
      )}

      {/* Render iframe if present */}
      {column.iframeSrc && (
        <div className="m-0 flex flex-col items-center w-full">
          <iframe
            src={column.iframeSrc}
            title={column.iframeTitle || "Embedded Content"}
            width="100%"
            height={column.iframeHeight || "600px"}
            className="rounded-lg shadow-sm border-0"
            style={{ maxWidth: "100%" }}
            allowFullScreen={column.allowFullScreen !== false}
            loading="lazy"
          />
          {column.iframeCaption && <p className="text-sm text-gray-500 mt-2 text-center">{column.iframeCaption}</p>}
        </div>
      )}
    </motion.div>
  )
}

// Content Block component
const ContentBlock = ({ block, index, currentLine }) => {
  const blockRef = useRef(null)

  // Check if block should be highlighted
  const isHighlighted =
    block.highlight ||
    (block.subtitleRange && currentLine >= block.subtitleRange[0] && currentLine <= block.subtitleRange[1])

  // Get block styling
  const style = block.style || {}

  // DIRECT MARGIN HANDLING - Apply margin directly if specified in style
  const marginStyle = {}
  if (style.margin) {
    // Handle mt-6 and similar classes by converting to marginTop style
    if (style.margin.startsWith("mt-")) {
      const value = style.margin.replace("mt-", "")
      marginStyle.marginTop = `${Number.parseInt(value) * 0.25}rem` // Convert to rem (Tailwind uses 0.25rem increments)
    } else if (style.margin.startsWith("mb-")) {
      const value = style.margin.replace("mb-", "")
      marginStyle.marginBottom = `${Number.parseInt(value) * 0.25}rem`
    } else if (style.margin.startsWith("ml-")) {
      const value = style.margin.replace("ml-", "")
      marginStyle.marginLeft = `${Number.parseInt(value) * 0.25}rem`
    } else if (style.margin.startsWith("mr-")) {
      const value = style.margin.replace("mr-", "")
      marginStyle.marginRight = `${Number.parseInt(value) * 0.25}rem`
    }
  }

  // Get CSS classes from block properties (excluding margin which we handle separately)
  const blockClasses = getClassesFromProps(
    { ...block, ...style, margin: null }, // Set margin to null to exclude it from classes
    { padding: "p-0", rounded: "rounded-lg", fontSize: "text-base", fontWeight: "font-normal" },
  )

  // Get inline styles from block properties
  const blockStyles = {
    ...getStyleFromProps({ ...block, ...style }),
    ...marginStyle, // Add our direct margin styles
  }

  // Animation settings
  const animation = {
    initial: { opacity: 0, y: 10 },
    animate: {
      opacity: 1,
      y: 0,
      scale: isHighlighted ? 1.02 : 1,
      boxShadow: isHighlighted ? "0 4px 12px rgba(0,0,0,0.1)" : "none",
    },
    transition: { delay: index * 0.05, duration: 0.2 },
  }

  // Render content based on its type
  // Example implementation for rendering a column with proper icon and image handling
  const renderColumn = (column) => {
    // Handle direct HTML content if present
    if (column.content) {
      // Use DOMPurify instead of sanitize-html
      const sanitizedHtml = DOMPurify.sanitize(column.content, {
        ALLOWED_TAGS: ["b", "i", "em", "strong", "a", "p", "br", "ul", "ol", "li", "span"],
        ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
      })
      return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
    }

    // Handle nested rows if present
    if (column.rows) {
      return (
        <div className="flex flex-wrap">
          {column.rows.map((row, rowIndex) => (
            <div key={rowIndex} className="flex flex-wrap">
              {row.columns.map((nestedColumn, colIndex) => (
                <div key={colIndex} className="p-4 rounded-lg">
                  {renderColumnContent(nestedColumn)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )
    }

    // Regular column content
    return <div className="p-4 rounded-lg">{renderColumnContent(column)}</div>
  }

  // Render image with potential icon overlay
  const renderImage = (column) => {
    if (!column.imageSrc) return null

    return (
      <div className="relative mb-2">
        {column.icon && column.iconPositionStyles && (
          <div style={column.iconPositionStyles} className=" z-10">
            {renderIconWithBackground(column.icon, {
              style: column.iconStyle,
              bgColor: column.iconBgColor,
              color: column.iconColor,
              size: column.iconSize || 24,
            })}
          </div>
        )}

        <img src={column.imageSrc || "/placeholder.svg"} alt={column.imageAlt || ""} className="w-full block" />

        {column.imageCaption && <p className="text-center mt-2 text-gray-600">{column.imageCaption}</p>}
      </div>
    )
  }

  // Render grid layout
  const renderGrid = () => {
    if (!block.columns || !block.columns.length) return null

    // Get layout classes
    const gridClass = getLayoutClasses(block.layout || {})

    return (
      <div className={gridClass}>
        {block.columns.map((column, i) => (
          <ColumnContent key={i} column={column} index={i} currentLine={currentLine} />
        ))}
      </div>
    )
  }

  // Render block icon (Lucide or custom)
  const renderBlockIcon = () => {
    if (!block.icon) return null

    // If iconStyle is circle-bg, render icon with background
    if (block.iconStyle === "circle-bg") {
      return renderIconWithBackground(block.icon, {
        backgroundColor: block.iconBgColor || "#E74C3C",
        iconColor: block.iconColor || "white",
        size: Number.parseInt(block.iconSize) || 40,
        padding: 8,
      })
    }

    // If icon is a string and contains a dash, assume it's a Lucide icon name
    if (typeof block.icon === "string" && block.icon.indexOf("-") > -1) {
      return renderLucideIcon(kebabToPascalCase(block.icon), {
        size: Number.parseInt(block.iconSize) || 24,
        color: block.iconColor || "currentColor",
        className: block.iconClassName || "text-2xl",
      })
    }

    // Otherwise, just render the icon as is (emoji or other content)
    return <span className="block-icon mr-1 text-2xl">{block.icon}</span>
  }

  // Debug output for margin
  console.log(`Block ${index} margin:`, style.margin, "Applied style:", marginStyle)

  return (
    <motion.div
      ref={blockRef}
      {...animation}
      className={`content-block ${blockClasses} ${isHighlighted ? "content-block-highlighted" : ""}`}
      style={blockStyles}
    >
      {/* Block header */}
      {(block.title || block.icon) && (
        <div className={`block-header mb-1 ${block.textAlignment ? `text-${block.textAlignment}` : ""}`}>
          {block.icon && renderBlockIcon()}
          {block.title && (
            <h2
              className={`block-title ${getClassesFromProps({ fontSize: block.titleFontSize, fontWeight: "semibold" }, { fontSize: "text-xl", fontWeight: "font-semibold" })} w-full m-0`}
            >
              {block.title}
            </h2>
          )}
        </div>
      )}

      {/* Block description */}
      {block.description && (
        <div className={`block-description mb-1 ${block.textAlignment ? `text-${block.textAlignment}` : ""} w-full`}>
          <p
            className={`${getClassesFromProps({ fontSize: block.descriptionFontSize }, { fontSize: "text-base" })} m-0`}
          >
            {parseContent(block.description)}
          </p>
        </div>
      )}

      {/* Block content */}
      {block.content && (
        <div className="block-content mb-1 m-0">
          {typeof block.content === "string" ? parseContent(block.content) : JSON.stringify(block.content)}
        </div>
      )}
      {/* Render items as a list */}
      {block.items &&
        !block.layout?.type &&
        renderListItems(block.items, {
          isNumbered: block.listType === "numbered",
          highlightItem: block.highlightItem,
          currentLine: currentLine,
        })}

      {/* Render advanced layout with rows if present */}
      {block.rows && (
        <div className="flex flex-wrap">
          {block.rows.map((row, rowIndex) => (
            <div key={rowIndex} className="flex flex-wrap">
              {row.columns.map((nestedColumn, colIndex) => (
                <div key={colIndex} className="p-4 rounded-lg">
                  {renderColumnContent(nestedColumn)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Render layout based on type */}
      {block.layout?.type && renderGrid()}
    </motion.div>
  )
}

// Function to render column content
const renderColumnContent = (column) => {
  // Handle direct HTML content if present
  if (column.content) {
    // Use DOMPurify instead of sanitize-html
    const sanitizedHtml = DOMPurify.sanitize(column.content, {
      ALLOWED_TAGS: ["b", "i", "em", "strong", "a", "p", "br", "ul", "ol", "li", "span"],
      ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
    })
    return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
  }

  // Handle nested rows if present
  if (column.rows) {
    return (
      <div className="flex flex-wrap">
        {column.rows.map((row, rowIndex) => (
          <div key={rowIndex} className="flex flex-wrap">
            {row.columns.map((nestedColumn, colIndex) => (
              <div key={colIndex} className="p-4 rounded-lg">
                {renderColumnContent(nestedColumn)}
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  // Regular column content
  return <div className="p-4 rounded-lg">{column.content}</div>
}

// Function to get column classes
const getColumnClasses = (column) => {
  return getClassesFromProps(column, { padding: "p-4", rounded: "rounded-lg" })
}

// Function to get nested column classes
const getNestedColumnClasses = (nestedColumn) => {
  return getClassesFromProps(nestedColumn, { padding: "p-4", rounded: "rounded-lg" })
}

// Main Content Template component
const ModernContentTemplate = ({ content, currentLine }) => {
  const containerRef = useRef(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const videoContainerRef = useRef(null)

  const toggleFullscreen = () => {
    if (videoContainerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      } else {
        videoContainerRef.current.requestFullscreen()
      }
      setIsFullscreen(!isFullscreen)
    }
  }

  // Get global spacing class
  const globalSpacingClass = getClassesFromProps({ globalSpacing: content?.spacing }, { globalSpacing: "space-y-0" })

  // Function to render content blocks in the order they appear in the JSON
  const renderContentInOrder = () => {
    const renderedComponents = []

    // Render video if present
    if (content?.videoUrl || content?.videoSrc) {
      renderedComponents.push(
        <div key="video" className="video-container relative m-0" ref={videoContainerRef}>
          <video src={content.videoUrl || content.videoSrc} controls className="w-full rounded-xl" />
          <button
            onClick={toggleFullscreen}
            className="absolute top-2 right-2 bg-black/50 text-white p-1 rounded-full hover:bg-black/70 transition-colors"
            aria-label="Toggle fullscreen"
          >
            {isFullscreen ? <X size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>,
      )
    }

    // Render advanced rows layout if present at the top level
    if (content?.rows && content.rows.length > 0) {
      renderedComponents.push(
        <div key="rows" className="content-rows">
          {content.rows.map((row, rowIndex) => (
            <Row key={rowIndex} row={row} index={rowIndex} currentLine={currentLine} />
          ))}
        </div>,
      )
    }

    // Render blocks
    if (content?.blocks && Array.isArray(content.blocks)) {
      renderedComponents.push(
        <div key="blocks" className={`content-blocks ${globalSpacingClass}`}>
          {content.blocks.map((block, index) => (
            <ContentBlock key={index} block={block} index={index} currentLine={currentLine} />
          ))}
        </div>,
      )
    }

    // Render columns layout
    if (content?.columns && content.columns.length > 0) {
      // Get layout settings
      const layout = content.layout || {}

      // Support for custom CSS variables for grid layouts
      const customStyles = {}
      if (layout.gridAreas) customStyles["--grid-areas"] = layout.gridAreas
      if (layout.gridColumns) customStyles["--grid-columns"] = layout.gridColumns
      if (layout.gridRows) customStyles["--grid-rows"] = layout.gridRows

      // Get layout classes
      const gridClass = getLayoutClasses({
        type: layout.type || "grid",
        columns: layout.columns || content.columns.length,
        spacing: layout.spacing || "normal",
        alignment: layout.alignment,
        responsive: layout.responsive,
        minColumnWidth: layout.minColumnWidth,
        customGrid: layout.customGrid,
        flexDirection: layout.flexDirection,
        flexGrow: layout.flexGrow,
        flexShrink: layout.flexShrink,
        customClass: layout.customClass,
      })

      renderedComponents.push(
        <div
          key="columns"
          className={`${gridClass} ${layout.containerClass || ""}`}
          style={{
            ...customStyles,
            ...(layout.containerStyle || {}),
          }}
        >
          {content.columns.map((column, index) => (
            <ColumnContent key={index} column={column} index={index} currentLine={currentLine} />
          ))}
        </div>,
      )
    }

    return renderedComponents
  }

  return (
    <BaseTemplate content={content}>
      <div ref={containerRef} className={`modern-content-container ${globalSpacingClass}`}>
        {renderContentInOrder()}
      </div>
    </BaseTemplate>
  )
}

// Main Selector Component
const ModernTemplateSelector = ({ type, content, currentLine, ...props }) => {
  // For now we only have the content template
  // You can add more specialized templates as needed
  return <ModernContentTemplate content={content} currentLine={currentLine} {...props} />
}

// Add CSS for responsive grid columns and masonry layout
if (typeof document !== "undefined") {
  // Only run in browser environment
  const style = document.createElement("style")
  style.textContent = `
    /* Responsive grid columns */
    .grid-template-columns-250px {
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    }
    .grid-template-columns-300px {
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    }
    .grid-template-columns-350px {
      grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    }
    .grid-template-columns-400px {
      grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    }
    
    /* Masonry layout */
    .masonry-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      grid-gap: 1rem;
      grid-auto-flow: dense;
      grid-auto-rows: minmax(50px, auto);
    }
    
    /* Column highlight effect */
    .column-highlighted {
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      z-index: 1;
    }
    
    /* Row styling */
    .row {
      width: 100%;
      display: flex;
      flex-direction: column;
    }
    
    @media (min-width: 768px) {
      .row {
        flex-direction: row;
      }
    }
  `
  document.head.appendChild(style)
}

export default ModernTemplateSelector
